import React, { useState, useEffect } from 'react';
import { Percent, Info, DollarSign } from 'lucide-react';
import { supabase } from '../lib/supabase';

interface Props {
  feeResponsibility: 'COUPLE' | 'GUEST' | 'SPLIT';
  platformFeePercentage: number;
  gatewayFeePercentage: number;
  onChange: (values: {
    feeResponsibility: 'COUPLE' | 'GUEST' | 'SPLIT';
    platformFeePercentage: number;
    gatewayFeePercentage: number;
  }) => void;
  currency: string;
  isAdmin?: boolean;
}

export default function FeeSettingsForm({
  feeResponsibility,
  platformFeePercentage,
  gatewayFeePercentage,
  onChange,
  currency,
  isAdmin = false
}: Props) {
  const [defaultPlatformFee, setDefaultPlatformFee] = useState<number>(20);
  const [defaultGatewayFee, setDefaultGatewayFee] = useState<number>(20);

  useEffect(() => {
    loadDefaultFees();
  }, []);

  const loadDefaultFees = async () => {
    try {
      // Get platform fee rate
      const { data: platformFeeData, error: platformFeeError } = await supabase
        .from('system_settings')
        .select('value')
        .eq('key', 'platform_fee_rate')
        .single();
        
      if (!platformFeeError && platformFeeData?.value) {
        setDefaultPlatformFee(parseFloat(platformFeeData.value));
      }
      
      // Get gateway fee rate
      const { data: gatewayFeeData, error: gatewayFeeError } = await supabase
        .from('system_settings')
        .select('value')
        .eq('key', 'gateway_fee_rate')
        .single();
        
      if (!gatewayFeeError && gatewayFeeData?.value) {
        setDefaultGatewayFee(parseFloat(gatewayFeeData.value));
      }
    } catch (error) {
      console.error('Error loading default fee rates:', error);
    }
  };

  const handleFeeResponsibilityChange = (value: 'COUPLE' | 'GUEST' | 'SPLIT') => {
    onChange({
      feeResponsibility: value,
      platformFeePercentage,
      gatewayFeePercentage
    });
  };

  const handlePlatformFeeChange = (value: number) => {
    onChange({
      feeResponsibility,
      platformFeePercentage: value,
      gatewayFeePercentage
    });
  };

  const handleGatewayFeeChange = (value: number) => {
    onChange({
      feeResponsibility,
      platformFeePercentage,
      gatewayFeePercentage: value
    });
  };

  const handleUseDefaultFees = () => { 
    onChange({
      feeResponsibility,
      platformFeePercentage: defaultPlatformFee,
      gatewayFeePercentage: defaultGatewayFee
    });
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center space-x-2">
        <Percent className="w-5 h-5 text-pastel-purple-600" />
        <h3 className="text-lg font-medium text-gray-800 font-poppins">Service Fee Settings</h3>
      </div>

      <div className="card-pastel p-4 rounded-lg">
        <div className="flex items-center space-x-2 mb-3">
          <Info className="w-4 h-4 text-pastel-blue-600" />
          <p className="text-sm text-pastel-blue-600">
            Service fees help cover platform maintenance and payment processing costs.
          </p>
        </div>
      </div>

      <div className="space-y-4"> 
        <div> 
          <label className="block text-sm font-medium text-gray-600 mb-2">
            Fee Responsibility *
          </label>
          <div className="space-y-2">
            <label className="flex items-center p-3 bg-white/60 border border-white/40 rounded-md cursor-pointer hover:bg-white/80 transition-colors">
              <input
                type="radio"
                name="feeResponsibility"
                checked={feeResponsibility === 'GUEST'}
                onChange={() => handleFeeResponsibilityChange('GUEST')}
                className="h-4 w-4 text-primary focus:ring-primary border-white/40 bg-white/60"
              />
              <div className="ml-3">
                <span className="block text-gray-800 font-medium">Guest Pays Fee</span>
                <span className="block text-sm text-gray-500">Fees are added to the gift amount</span>
              </div>
            </label>

            <label className="flex items-center p-3 bg-white/60 border border-white/40 rounded-md cursor-pointer hover:bg-white/80 transition-colors">
              <input
                type="radio"
                name="feeResponsibility"
                checked={feeResponsibility === 'COUPLE'}
                onChange={() => handleFeeResponsibilityChange('COUPLE')}
                className="h-4 w-4 text-primary focus:ring-primary border-white/40 bg-white/60"
              />
              <div className="ml-3">
                <span className="block text-gray-800 font-medium">Couple Absorbs Fee</span>
                <span className="block text-sm text-gray-500">Fees are deducted from the gift amount</span>
              </div>
            </label>

            <label className="flex items-center p-3 bg-white/60 border border-white/40 rounded-md cursor-pointer hover:bg-white/80 transition-colors">
              <input
                type="radio"
                name="feeResponsibility"
                checked={feeResponsibility === 'SPLIT'}
                onChange={() => handleFeeResponsibilityChange('SPLIT')}
                className="h-4 w-4 text-primary focus:ring-primary border-white/40 bg-white/60"
              />
              <div className="ml-3">
                <span className="block text-gray-800 font-medium">Split 50/50</span>
                <span className="block text-sm text-gray-500">Fees are divided between guest and couple</span>
              </div>
            </label>
          </div>
        </div>

        {isAdmin ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-600 mb-2">
                Platform Fee Percentage (%) *
              </label>
              <div className="relative">
                <input
                  type="number"
                  value={platformFeePercentage}
                  onChange={(e) => handlePlatformFeeChange(parseFloat(e.target.value) || 0)}
                  min="0"
                  max="100"
                  step="0.01"
                  className="input-pastel"
                  required
                />
                <span className="absolute right-3 top-2.5 text-gray-500">%</span>
              </div>
              <p className="text-xs text-gray-500 mt-1">
                Platform fee for using Giftyfy services
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-600 mb-2">
                Payment Gateway Fee Percentage (%) *
              </label>
              <div className="relative">
                <input
                  type="number"
                  value={gatewayFeePercentage}
                  onChange={(e) => handleGatewayFeeChange(parseFloat(e.target.value) || 0)}
                  min="0"
                  max="100"
                  step="0.01"
                  className="input-pastel"
                  required
                />
                <span className="absolute right-3 top-2.5 text-gray-500">%</span>
              </div>
              <p className="text-xs text-gray-500 mt-1">
                Payment processor fee for handling transactions
              </p>
            </div>
          </div>
        ) : null}

        {isAdmin ? (
          <div className="flex justify-end">
            <button
              type="button"
              onClick={handleUseDefaultFees}
              className="text-sm text-pastel-purple-600 hover:text-pastel-purple-700 flex items-center space-x-1"
            > {/* This button is for the logo */}
              <span>Use default fees ({defaultPlatformFee}% + {defaultGatewayFee}%)</span>
            </button>
          </div>
        ) : null}
      </div>
    </div>
  );
}
